/**
 * "Add server" (D21): pick how long the one-time code lives (and whether the
 * new server is the site's LAN witness, D9), generate it, and copy the
 * `docker run` line that installs a node with it. The code is shown once — the
 * cloud keeps only a hash — with its expiry. A replacement's code (after
 * "Replace node") arrives already generated, in the revoked node's role.
 *
 * What an admin needs to know to run it is said here, where they copy it: the
 * line removes an old `acp` container first, so it can be re-run on the same
 * host (the volume keeps the node's identity; a new code replaces one the cloud
 * revoked); it runs in a Linux/macOS shell or PowerShell (not cmd.exe); and one
 * machine runs one server — host networking — so a backup needs a second host.
 */
import type { EnrolmentCodeWire } from '@acp/api-contract/sites.js';
import { CopyIcon } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../ui/button.js';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog.js';
import { Label } from '../ui/label.js';
import { NativeSelect } from '../ui/native-select.js';

/** The code lifetimes offered (D21: at most 24 h). */
export const CODE_LIFETIMES: readonly { readonly minutes: number; readonly label: string }[] = [
  { minutes: 30, label: '30 minutes' },
  { minutes: 60, label: '1 hour' },
  { minutes: 8 * 60, label: '8 hours' },
  { minutes: 24 * 60, label: '24 hours' },
];

export type AddServerDialogProps = {
  readonly open: boolean;
  readonly siteName: string;
  /** The generated code, or null before one. */
  readonly enrolment: EnrolmentCodeWire | null;
  readonly pending: boolean;
  readonly error: string | null;
  readonly onGenerate: (request: { readonly minutes: number; readonly witness: boolean }) => void;
  readonly onCopy: (text: string) => void;
  readonly onOpenChange: (open: boolean) => void;
};

function Generated({ enrolment, onCopy, onOpenChange }: { readonly enrolment: EnrolmentCodeWire; readonly onCopy: (text: string) => void; readonly onOpenChange: (open: boolean) => void }) {
  const role = enrolment.role === null ? 'the pairing order (first main, then backup)' : enrolment.role;
  return (
    <div className="grid gap-4">
      <DialogHeader>
        <DialogTitle>{enrolment.replacesNodeId === null ? 'Install the server' : 'Install the replacement'}</DialogTitle>
        <DialogDescription>
          Run this on the new host (Docker with host networking: Linux, or Docker Desktop). It works once, until {enrolment.expiresAt.slice(0, 16).replace('T', ' ')} UTC; the node takes {role}.
        </DialogDescription>
      </DialogHeader>
      <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-all rounded-md border bg-muted p-3 font-mono text-xs" data-testid="docker-run">
        {enrolment.dockerRun}
      </pre>
      <ul className="grid list-disc gap-1 pl-5 text-xs text-muted-foreground" data-testid="install-notes">
        <li>Paste it into a terminal on the host: a Linux or macOS shell, or PowerShell with Docker Desktop on Windows (not cmd.exe).</li>
        <li>It removes an old <span className="font-mono">acp</span> container on that host first, so running it again — or on a host whose server you replaced — is fine (the first time, Docker just says there was none).</li>
        <li>
          The server keeps its identity and data in the Docker volume <span className="font-mono">acp-data</span>. A new code re-enrols a server the cloud revoked; one still active keeps its identity. To start
          from nothing, remove the container and then <span className="font-mono">docker volume rm acp-data</span>.
        </li>
        <li>It shows in the server list as “waiting for the server to connect…” until it enrols.</li>
      </ul>
      <p className="text-xs text-muted-foreground">This is the only time the code is shown: the cloud keeps just a hash of it.</p>
      <DialogFooter>
        <Button variant="outline" onClick={() => onCopy(enrolment.dockerRun)} data-testid="copy-docker-run">
          <CopyIcon />
          Copy command
        </Button>
        <Button onClick={() => onOpenChange(false)}>Done</Button>
      </DialogFooter>
    </div>
  );
}

function Options({ siteName, pending, error, onGenerate, onOpenChange }: Omit<AddServerDialogProps, 'open' | 'enrolment' | 'onCopy'>) {
  const [minutes, setMinutes] = useState(24 * 60);
  const [witness, setWitness] = useState(false);
  return (
    <div className="grid gap-4">
      <DialogHeader>
        <DialogTitle>Add a server to {siteName}</DialogTitle>
        <DialogDescription>The first server becomes main, the second backup. A third can only join as the site’s LAN witness, which lets the pair fail over in about a second.</DialogDescription>
      </DialogHeader>
      <p className="text-sm text-muted-foreground" data-testid="one-per-host">
        One machine runs one server: it uses the host’s network, so a main and a backup on the same machine would take the same ports. Put the backup (and a witness) on another computer or VM — to try a pair on a single
        Docker host, see “A pair on one Docker host” in the sites guide (docs/acp/sites.md).
      </p>
      <div className="grid gap-2">
        <Label htmlFor="code-lifetime">The code works for</Label>
        <NativeSelect id="code-lifetime" value={String(minutes)} onChange={(e) => setMinutes(Number(e.target.value))}>
          {CODE_LIFETIMES.map((l) => (
            <option key={l.minutes} value={l.minutes}>
              {l.label}
            </option>
          ))}
        </NativeSelect>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={witness} onChange={(e) => setWitness(e.target.checked)} data-testid="witness-code" />
        This server is the site’s LAN witness
      </label>
      {error === null ? null : (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
          Cancel
        </Button>
        <Button onClick={() => onGenerate({ minutes, witness })} disabled={pending} data-testid="generate-code">
          {pending ? 'Generating…' : 'Generate install command'}
        </Button>
      </DialogFooter>
    </div>
  );
}

export function AddServerDialog(props: AddServerDialogProps) {
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        {!props.open ? null : props.enrolment === null ? (
          <Options siteName={props.siteName} pending={props.pending} error={props.error} onGenerate={props.onGenerate} onOpenChange={props.onOpenChange} />
        ) : (
          <Generated enrolment={props.enrolment} onCopy={props.onCopy} onOpenChange={props.onOpenChange} />
        )}
      </DialogContent>
    </Dialog>
  );
}
